import { useMemo } from 'react'
import {
  CheckboxLabel,
  FormGrid,
  HybridSelectContainer,
  Label,
  RemovePillButton,
  SearchableSection,
  SearchInput,
  SearchResultsGrid,
  SearchPill,
  SelectedPill,
  StaticOptionsGrid,
  StyledAsyncSelect,
  StyledTextarea,
} from './DiagnosticAssessmentSubSection.styled'
import { useFieldMap } from '../../hooks/useFieldMap'
import { useLoadOptions } from '../../hooks/useLoadOptions'
import { useSearchableOptions } from '../../hooks/useSearchableOptions'

export default function DiagnosticAssessmentSubSection({
  fieldValues,
  handleFieldValueChange,
  subSection,
}) {
  const diagnosticSubSection = useMemo(() => {
    return subSection
  }, [subSection])

  // Use custom hook for field mapping
  const fields = useFieldMap(diagnosticSubSection)

  console.log({ diagnosticSubSection, subSection, fieldValues, fields })

  // Get selected tools as array (memoized to prevent re-renders)
  const selectedTools = useMemo(
    () => fieldValues?.toolIds?.valueStringList || [],
    [fieldValues?.toolIds?.valueStringList],
  )

  // Use custom hook for searchable options
  const { searchText, setSearchText, searchResults, isSearching } = useSearchableOptions(
    fields.toolIds,
    'searchAssessmentTools',
    selectedTools,
    15, // pageSize
    false, // hideSelected - keep selected pills visible
    true, // hideStatic - hide static checkbox options from search results
  )

  // Use custom hook for loading referral provider options
  const loadReferralProviders = useLoadOptions(fields.referToId, 'searchReferralProviders', 20)

  const toggleStaticOption = (code, display) => {
    const isSelected = selectedTools.some((t) => t.code === code)

    if (isSelected) {
      // Remove
      handleFieldValueChange(
        (prev) => ({
          ...prev,
          valueStringList: prev?.valueStringList?.filter((t) => t.code !== code) || [],
        }),
        'toolIds',
      )
    } else {
      // Add
      handleFieldValueChange(
        (prev) => ({
          ...prev,
          valueStringList: [...(prev?.valueStringList || []), { code, display }],
        }),
        'toolIds',
      )
    }
  }

  const addSearchedTool = (code, display) => {
    if (!code) return

    const exists = selectedTools.some((t) => t.code === code)
    if (exists) return

    handleFieldValueChange(
      (prev) => ({
        ...prev,
        valueStringList: [...(prev?.valueStringList || []), { code, display }],
      }),
      'toolIds',
    )
  }

  const removeTool = (code) => {
    handleFieldValueChange(
      (prev) => ({
        ...prev,
        valueStringList: prev?.valueStringList?.filter((t) => t.code !== code) || [],
      }),
      'toolIds',
    )
  }

  return (
    <FormGrid>
      {/* Reason Field */}
      <Label>{fields.reason?.label}</Label>
      <StyledTextarea
        value={fieldValues?.reason?.valueString || ''}
        onChange={(e) =>
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              valueString: e.target.value,
            }),
            'reason',
          )
        }
        placeholder={fields.reason?.placeholder}
      />

      {/* Assessment Tools - HYBRID Multi-Select */}
      <Label>{fields.toolIds?.label}</Label>
      <HybridSelectContainer>
        {/* Static Options as Checkboxes */}
        <StaticOptionsGrid>
          {fields.toolIds?.optionSource?.staticOptions?.map((opt) => (
            <CheckboxLabel key={opt.code}>
              <input
                type="checkbox"
                checked={selectedTools.some((t) => t.code === opt.code)}
                onChange={() => toggleStaticOption(opt.code, opt.display)}
              />
              {opt.display}
            </CheckboxLabel>
          ))}
        </StaticOptionsGrid>

        {/* Search for Additional Tools */}
        <SearchableSection>
          <SearchInput
            type="text"
            placeholder="Search more tools..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {/* Search Results Pills */}
          {isSearching && <div style={{ marginTop: '12px', color: '#6b7280' }}>Searching...</div>}

          {!isSearching && searchResults.length > 0 && (
            <SearchResultsGrid>
              {searchResults.map((result) => (
                <SearchPill
                  key={result.value}
                  type="button"
                  onClick={() => addSearchedTool(result.value, result.label)}
                >
                  {result.label}
                </SearchPill>
              ))}
            </SearchResultsGrid>
          )}

          {!isSearching && searchText && searchResults.length === 0 && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
              No results found for &quot;{searchText}&quot;
            </div>
          )}
        </SearchableSection>

        {/* Show Selected Pills */}
        {selectedTools.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {selectedTools.map((tool) => (
              <SelectedPill key={tool.code}>
                {tool.display}
                <RemovePillButton onClick={() => removeTool(tool.code)}>×</RemovePillButton>
              </SelectedPill>
            ))}
          </div>
        )}
      </HybridSelectContainer>

      {/* Refer To - Async Select */}
      <Label>{fields.referToId?.label}</Label>
      <StyledAsyncSelect
        classNamePrefix="react-select"
        value={
          fieldValues?.referToId?.code
            ? {
                value: fieldValues.referToId.code,
                label: fieldValues.referToId.display || fieldValues.referToId.code,
              }
            : null
        }
        onChange={(selected) => {
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              code: selected?.value || '',
              display: selected?.label || '',
            }),
            'referToId',
          )
        }}
        loadOptions={loadReferralProviders}
        debounceTimeout={300}
        placeholder="Search specialists..."
        isClearable
        additional={{ page: 1 }}
      />

      {/* Notes Field */}
      <Label>{fields.notes?.label}</Label>
      <StyledTextarea
        value={fieldValues?.notes?.valueString || ''}
        onChange={(e) =>
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              valueString: e.target.value,
            }),
            'notes',
          )
        }
        placeholder={fields.notes?.placeholder}
      />
    </FormGrid>
  )
}
